import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, Form, Input, Modal, Row, Spin } from "antd";
import moment from "moment";

import DetailTabs from "@components/DetailTabs";
import RecordAuditTimeline from "@components/Table/RecordAuditLog";
import globalMessage from "@/utils/globalMessage";
import { api } from "@redux/api";
import { useAuth, AUTH_LEVEL } from "@/hooks/use-auth";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import "./index.css";

const LIST_PATH = "/admin/service-category";

const ServiceCategoryDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { closeTab, refreshTab } = useKeepAliveContext();
  const canWrite = useAuth("serviceCategory", AUTH_LEVEL.WRITE);
  const canFullAccess = useAuth("serviceCategory", AUTH_LEVEL.FULL_ACCESS);
  const isCreatePage = isAdd === true;

  const [loading, setLoading] = useState(!isCreatePage);
  const [readOnly, setReadOnly] = useState(!isCreatePage);
  const [detailData, setDetailData] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("1");

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const response = await api.getServiceCategory(id);
      const data = response.data;
      setDetailData(data);
      form.setFieldsValue(data);
    } catch (error) {
      globalMessage.error({ message: "Error", description: "Failed to load service category" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCreatePage) {
      setLoading(false);
    } else if (id) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isCreatePage]);

  const handleCancel = () => {
    if (isCreatePage) {
      closeTab();
      navigate(LIST_PATH);
      return;
    }
    setReadOnly(true);
    form.setFieldsValue(detailData || {});
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const payload = {
        name: values.name,
        comment: values.comment || "",
      };
      const response = isCreatePage
        ? await api.createServiceCategory(payload)
        : await api.editServiceCategory(id, payload);

      if (response.status === 200 || response.status === 201) {
        globalMessage.success({
          message: "Success",
          description: isCreatePage
            ? "Service category created successfully"
            : "Service category updated successfully",
        });
        refreshTab(LIST_PATH);
        if (isCreatePage) {
          closeTab();
          navigate(LIST_PATH);
        } else {
          setReadOnly(true);
          fetchDetail();
        }
      }
    } catch (error) {
      globalMessage.error({ message: "Error", description: "Failed to save service category" });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this service category?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true);
          await api.deleteServiceCategory(id);
          globalMessage.success({ message: "Success", description: "Service category deleted successfully" });
          refreshTab(LIST_PATH);
          closeTab();
          navigate(LIST_PATH);
        } catch (error) {
          globalMessage.error({ message: "Error", description: "Failed to delete service category" });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const operationButtons = () => {
    if (isCreatePage) {
      return (
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()}>Save</Button>
        </div>
      );
    }
    if (readOnly) {
      return (
        <div style={{ display: "flex", gap: 8 }}>
          {canWrite && <Button type="primary" onClick={() => setReadOnly(false)}>Edit</Button>}
          {canFullAccess && <Button danger onClick={handleDelete}>Delete</Button>}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", gap: 8 }}>
        <Button type="primary" onClick={() => form.submit()}>Save</Button>
        <Button onClick={handleCancel}>Cancel</Button>
      </div>
    );
  };

  const detailContent = (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="horizontal"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      className="detailForm"
      autoComplete="off"
    >
      <Row gutter={[0, 0]}>
        {!isCreatePage && (
          <>
            <Col xs={24} lg={12}>
              <Form.Item label="ID" name="id"><Input disabled /></Form.Item>
            </Col>
            <Col xs={0} lg={12} />
          </>
        )}
        <Col xs={24} lg={12}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "This field is required" }]}>
            <Input disabled={readOnly && !isCreatePage} allowClear />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label="Comment" name="comment">
            <Input.TextArea rows={4} disabled={readOnly && !isCreatePage} allowClear />
          </Form.Item>
        </Col>
        {!isCreatePage && (
          <>
            <Col xs={24} lg={12}><Form.Item label="Created By"><span>{detailData?.createdby_name || ""}</span></Form.Item></Col>
            <Col xs={24} lg={12}><Form.Item label="Created Date"><span>{detailData?.created_date ? moment(detailData.created_date).format("YYYY-MM-DD HH:mm") : ""}</span></Form.Item></Col>
            <Col xs={24} lg={12}><Form.Item label="Modified By"><span>{detailData?.lastModified_name || ""}</span></Form.Item></Col>
            <Col xs={24} lg={12}><Form.Item label="Modified Date"><span>{detailData?.modified_date ? moment(detailData.modified_date).format("YYYY-MM-DD HH:mm") : ""}</span></Form.Item></Col>
          </>
        )}
      </Row>
    </Form>
  );

  if (loading && !detailData && !isCreatePage) {
    return <div style={{ textAlign: "center", padding: "100px 0" }}><Spin size="large" /></div>;
  }

  const items = [
    {
      key: "1",
      label: <span><i className="fa-regular fa-rectangle-list pr-2" />Detail</span>,
      children: <div className="service-category-detail-inner slds-m-around_x-small">{detailContent}</div>,
    },
    ...(!isCreatePage ? [{
      key: "2",
      label: <span><i className="fa-solid fa-clock-rotate-left pr-2" />Audit Log</span>,
      children: <div className="px-4"><RecordAuditTimeline moduleId={id} module="ServiceCategory" /></div>,
    }] : []),
  ];

  return (
    <div className="service-category-detail-container">
      <Spin spinning={loading}>
        <DetailTabs
          defaultActiveKey="1"
          destroyInactiveTabPane
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          tabBarExtraContent={activeTabKey === "1" ? operationButtons() : null}
          mobileThreshold={1}
          items={items}
        />
      </Spin>
    </div>
  );
};

export default ServiceCategoryDetail;
